import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ChatMessage } from '@/components/chat/message';

interface PendingPlanData {
  plan_text: string;
  brief_plan: string;
  full_plan: string;
  plan_id: string | null;
  cached: boolean;
  original_question: string;
}

// Progress message with optional technical details for admin view
export interface ProgressMessage {
  displayText: string;
  technicalDetails?: Record<string, string>;
}

interface ActiveChatState {
  // Persisted State
  messages: ChatMessage[];
  conversationId: string;
  currentMessage: ChatMessage | null;
  currentStatus: string;
  progressMessages: ProgressMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  pendingPlanMessageId: string | null;
  pendingPlanData: PendingPlanData | null;
  lastUserMessage: string;
  isGeneratingGraphs: boolean;
  graphGenerationMessageId: string | null;
  isGeneratingRecommendations: boolean;
  recommendationGenerationMessageId: string | null;

  // Non-persisted State
  abortController: AbortController | null;

  // Actions
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setConversationId: (id: string) => void;
  setCurrentMessage: (message: ChatMessage | null) => void;
  setCurrentStatus: (status: string) => void;
  setProgressMessages: (messages: ProgressMessage[] | ((prev: ProgressMessage[]) => ProgressMessage[])) => void;
  addProgressMessage: (message: string, technicalDetails?: Record<string, string>) => void;
  setIsLoading: (loading: boolean) => void;
  setIsStreaming: (streaming: boolean) => void;
  setPendingPlan: (messageId: string | null, data: PendingPlanData | null) => void;
  setLastUserMessage: (message: string) => void;
  setIsGeneratingGraphs: (generating: boolean) => void;
  setGraphGenerationMessageId: (id: string | null) => void;
  setIsGeneratingRecommendations: (generating: boolean) => void;
  setRecommendationGenerationMessageId: (id: string | null) => void;
  setAbortController: (controller: AbortController | null) => void;
  reset: () => void;
}

export const useActiveChatStore = create<ActiveChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      messages: [],
      conversationId: '',
      currentMessage: null,
      currentStatus: '',
      progressMessages: [],
      isLoading: false,
      isStreaming: false,
      pendingPlanMessageId: null,
      pendingPlanData: null,
      lastUserMessage: '',
      isGeneratingGraphs: false,
      graphGenerationMessageId: null,
      isGeneratingRecommendations: false,
      recommendationGenerationMessageId: null,
      abortController: null,

      // Actions
      setMessages: (messages) => {
        if (typeof messages === 'function') {
          set((state) => {
            const newMessages = messages(state.messages);
            // Deduplicate messages by ID (keep last occurrence to prevent duplicates from tab duplication)
            const seen = new Map<string, number>();
            newMessages.forEach((msg, idx) => {
              if (msg.id) seen.set(msg.id, idx);
            });
            const deduped = newMessages.filter((msg, idx) => 
              !msg.id || seen.get(msg.id) === idx
            );
            return { messages: deduped };
          });
        } else {
          // Deduplicate direct array too
          const seen = new Map<string, number>();
          messages.forEach((msg, idx) => {
            if (msg.id) seen.set(msg.id, idx);
          });
          const deduped = messages.filter((msg, idx) => 
            !msg.id || seen.get(msg.id) === idx
          );
          set({ messages: deduped });
        }
      },
      setConversationId: (id) => set({ conversationId: id }),
      setCurrentMessage: (message) => set({ currentMessage: message }),
      setCurrentStatus: (status) => set({ currentStatus: status }),
      setProgressMessages: (messages) => {
        if (typeof messages === 'function') {
          set((state) => ({ progressMessages: messages(state.progressMessages) }));
        } else {
          set({ progressMessages: messages });
        }
      },
      addProgressMessage: (message, technicalDetails) => {
        set((state) => ({
          progressMessages: [...state.progressMessages, { displayText: message, technicalDetails }]
        }));
      },
      setIsLoading: (loading) => set({ isLoading: loading }),
      setIsStreaming: (streaming) => set({ isStreaming: streaming }),
      setPendingPlan: (messageId, data) => set({ 
        pendingPlanMessageId: messageId, 
        pendingPlanData: data 
      }),
      setLastUserMessage: (message) => set({ lastUserMessage: message }),
      setIsGeneratingGraphs: (generating) => set({ isGeneratingGraphs: generating }),
      setGraphGenerationMessageId: (id) => set({ graphGenerationMessageId: id }),
      setIsGeneratingRecommendations: (generating) => set({ isGeneratingRecommendations: generating }),
      setRecommendationGenerationMessageId: (id) => set({ recommendationGenerationMessageId: id }),
      setAbortController: (controller) => set({ abortController: controller }),
      
      reset: () => {
        // Abort any ongoing stream
        const { abortController } = get();
        if (abortController) {
          abortController.abort();
        }
        
        set({
          messages: [],
          conversationId: '',
          currentMessage: null,
          currentStatus: '',
          progressMessages: [],
          isLoading: false,
          isStreaming: false,
          pendingPlanMessageId: null,
          pendingPlanData: null,
          lastUserMessage: '',
          isGeneratingGraphs: false,
          graphGenerationMessageId: null,
          isGeneratingRecommendations: false,
          recommendationGenerationMessageId: null,
          abortController: null,
        });
      },
    }),
    {
      name: 'active-chat-storage',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist these fields with data filtering
      partialize: (state) => {
        // Strip large query results to prevent quota exceeded errors
        const sanitizeMessage = (msg: ChatMessage): ChatMessage => {
          if (!msg.queries || msg.queries.length === 0) return msg;
          
          return {
            ...msg,
            queries: msg.queries.map(q => {
              // Keep query metadata, but limit result size
              const resultSize = JSON.stringify(q.result || '').length;
              
              // If result is larger than 10KB, truncate it
              if (resultSize > 10000) {
                return {
                  ...q,
                  result: {
                    _truncated: true,
                    _originalSize: resultSize,
                    _message: 'Large dataset truncated from storage. Refresh page to reload.',
                  }
                };
              }
              return q;
            })
          };
        };

        return {
          messages: state.messages.map(sanitizeMessage),
          conversationId: state.conversationId,
          currentMessage: state.currentMessage ? sanitizeMessage(state.currentMessage) : null,
          currentStatus: state.currentStatus,
          progressMessages: state.progressMessages,
          isLoading: state.isLoading,
          isStreaming: state.isStreaming,
          pendingPlanMessageId: state.pendingPlanMessageId,
          pendingPlanData: state.pendingPlanData,
          lastUserMessage: state.lastUserMessage,
          isGeneratingGraphs: state.isGeneratingGraphs,
          graphGenerationMessageId: state.graphGenerationMessageId,
          isGeneratingRecommendations: state.isGeneratingRecommendations,
          recommendationGenerationMessageId: state.recommendationGenerationMessageId,
        };
      },
    }
  )
);
